<template>
	<section class="custom-plan">
		<div class="plan-section">
			<div class="section-label">贷款城市</div>
			<nio-cell
				class="city-select"
				:title="cityName || '请选择城市'"
				clickable
				@click="$emit('city-picker-open')"
			>
				<template #right>
					<nio-icon :size="16" name="icon_arrows_right_2xs" />
				</template>
			</nio-cell>
		</div>

		<FinanceProductType
			class="plan-section"
			:financesList="customFinances"
			@change="handleProductChange"
		/>

		<template v-if="currentProduct">
			<!-- 首付比例 -->
			<DownPaymentRatio
				type="downPaymentRatio"
				:list="currentBank.down_payment_ratio || []"
				:ratio="financeInfo.down_payment_ratio"
				:amount="financeInfo.down_payment_amount"
				:is-ratio-along="currentProduct.is_ratio_along !== false"
				:amount-limit="currentProduct.down_payment_amount_limit || {}"
				@ratio-change="handleDownPaymentRatioChange"
				@amount-change="handleDownPaymentAmountChange"
			/>
			<!-- 尾款比例 -->
			<DownPaymentRatio
				:list="currentBank.tail_ratio || []"
				:ratio="financeInfo.tail_ratio"
				:amount="loanDetail.complete_money || ''"
				@ratio-change="v => updateFinanceInfo({ tail_ratio: v })"
			/>
			<!-- 贷款期数 -->
			<FinanceMonth
				:list="currentBank.month || []"
				:value="financeInfo.month"
				@change="updateFinanceInfo({ month: $event })"
			/>
			<!-- 贷款银行 -->
			<FinanceBank :list="bankList" :value="financeInfo.bank_id" @change="handleBankChange" />
			<!-- 月供 -->
			<MonthPayment :loanDetail="loanDetail" :description="currentProduct.description || ''" />
		</template>
	</section>
</template>

<script>
import { NioCell } from '@nio-fe/nio-app-ui'
import FinanceProductType from './FinanceProductType.vue'
import DownPaymentRatio from './DownPaymentRatio.vue'
import FinanceMonth from './FinanceMonth.vue'
import FinanceBank from './FinanceBank.vue'
import MonthPayment from './MonthPayment.vue'
import { mapState, mapActions } from 'vuex'
import nativeHybrid from '@/util/nativeHybrid'

export default {
	name: 'FinanceCustomPlan',
	components: {
		NioCell,
		FinanceProductType,
		DownPaymentRatio,
		FinanceMonth,
		FinanceBank,
		MonthPayment
	},
	props: {
		customFinances: {
			type: Array,
			default: () => []
		},
		city: {
			type: Object,
			default: () => ({})
		}
	},
	data() {
		return {
			// 当前选择的金融产品
			currentProduct: null,
			// 金融产品的信息
			financeInfo: {},
			paymentRequest: null,
			// 输入模式，'ratio' 表示按比例输入，'amount' 表示按金额输入
			inputMode: 'ratio'
		}
	},
	computed: {
		...mapState('financeCalcState', ['loanDetail', 'car']),
		totalAmount() {
			const car = this.car || {}
			const sellPrice = car.sell_programme && car.sell_programme.sell_price
			return Number(sellPrice) || 0
		},
		bankList() {
			return this.currentProduct && Array.isArray(this.currentProduct.bank_info_list)
				? this.currentProduct.bank_info_list
				: []
		},
		currentBank() {
			return this.bankList.find(bank => bank.bank_id === this.financeInfo.bank_id) || {}
		},
		cityName() {
			return this.city.name || ''
		}
	},
	watch: {
		customFinances: {
			immediate: true,
			handler(list) {
				const category = list[0]
				const product =
					category && Array.isArray(category.production_infos)
						? category.production_infos[0] || null
						: null
				this.handleProductChange({
					productType: category ? category.finance_category : null,
					product
				})
			}
		}
	},
	methods: {
		...mapActions('financeCalcState', ['calculatePayment']),
		getArrayFirst(list) {
			return list && list.length ? list[0] : ''
		},
		// 选择金融产品
		handleProductChange(selection) {
			this.inputMode = 'ratio'
			this.currentProduct = selection.product
			this.financeInfo = {}
			this.handleBankChange(this.bankList[0] || {})
		},
		// 选择银行
		handleBankChange(bank) {
			this.financeInfo = {
				bank_id: bank.bank_id || '',
				down_payment_ratio: this.getArrayFirst(bank.down_payment_ratio),
				down_payment_amount: this.getArrayFirst(bank.down_payment_ratio_amount),
				month: this.getArrayFirst(bank.month),
				tail_ratio: this.getArrayFirst(bank.tail_ratio),
				instalment_amount: this.getArrayFirst(bank.instalment)
					? this.getArrayFirst(bank.instalment).instalment_amount
					: ''
			}
			this.refreshPayment()
		},
		// 更新金融信息
		updateFinanceInfo(fields) {
			this.financeInfo = { ...this.financeInfo, ...fields }
			this.refreshPayment()
		},
		// 首付比例变化，计算首付金额
		handleDownPaymentRatioChange(ratio, index) {
			this.inputMode = 'ratio'
			const list = this.currentBank.down_payment_ratio_amount || []
			this.updateFinanceInfo({
				down_payment_ratio: ratio,
				down_payment_amount:
					list[index] || (this.totalAmount > 0 ? Math.ceil(this.totalAmount * Number(ratio)) : '')
			})
		},
		// 首付金额变化，计算首付比例
		handleDownPaymentAmountChange(amount) {
			this.inputMode = 'amount'
			const total = this.totalAmount
			if (!Number.isFinite(total) || total <= 0) return
			const ratio = Number((amount / total).toFixed(2))

			this.updateFinanceInfo({
				down_payment_amount: amount,
				down_payment_ratio: ratio
			})
		},
		async refreshPayment() {
			if (!this.currentProduct || !this.financeInfo.bank_id || !this.financeInfo.month) return
			const request = {}
			this.paymentRequest = request
			nativeHybrid.showLoading()
			try {
				const limit = this.currentProduct.down_payment_amount_limit || {}
				let params = {
					...this.financeInfo,
					product_code: this.currentProduct.product_code,
					down_payment_ratio_amount_min: limit.min_loan_amount,
					down_payment_ratio_amount_max: limit.max_loan_amount
				}

				if (this.inputMode === 'amount') {
					delete params.down_payment_ratio
				} else {
					delete params.down_payment_amount
				}
				// 获取月供信息
				await this.calculatePayment(params)
			} catch (error) {
				return
			} finally {
				if (this.paymentRequest === request) nativeHybrid.hideLoading()
			}
		}
	}
}
</script>

<style scoped lang="scss">
@import '~@nio-fe/nio-app-ui/es/style';

.custom-plan {
	margin-top: 16px;
	color: $text_primary;
}

.plan-section {
	margin: 0 20px;
}

.plan-section + .plan-section {
	margin-top: 20px;
}

.section-label {
	display: flex;
	align-items: flex-end;
	min-height: 25px;
	margin-bottom: 10px;
	color: $text_secondary;
	@include body_3(1.5, 400, false);
}

.city-select {
	box-sizing: border-box;
	min-height: 50px;
	border: 1px solid var(--border_divider_primary, #ebecee);
	padding: 13px 15px !important;
}

::v-deep {
	.nio-cell__left {
		flex: 1;
		min-width: 0;
	}

	.nio-cell__left-text {
		@include body_2(1.5, 400, false);
	}
}
</style>
